import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Icons } from '../assets/icons'
import { useInventory } from '../hooks/useInventory'
import { assetUrl, itemName } from '../utils/assetUrl'
import { ConvertToNft } from './ConvertToNft'
import { ItemDetail } from './ItemDetail'
import { ItemImage } from './ItemImage'

type InventoryCardProps = {
    nft: NFT
}

/**
 * Active items carry the gradient chrome, inactive ones stay neutral. That way the grid reads at
 * a glance — anything glowing is earning — and a wallet of 600 inactive items stays calm rather
 * than turning into 600 gradient buttons.
 */
export const InventoryCard = ({ nft }: InventoryCardProps) => {
    const { useStake, useUnstake } = useInventory()
    const { stake, isStaking } = useStake()
    const { unstake, isUnstaking } = useUnstake()

    const queryClient = useQueryClient()
    const [showing, setShowing] = useState(false)
    const [converting, setConverting] = useState(false)

    const isActive = !!nft.staked
    const isLoading = isStaking || isUnstaking
    // Absent on older responses, so only an explicit false means "not an NFT"
    // and only an explicit false means "cannot be staked".
    const extra = nft as NFT & { is_nft?: boolean; can_stake?: boolean; copies?: number }
    const isNft = extra.is_nft !== false
    const canStake = extra.can_stake !== false
    // Copies still held as skins. Rolling one you already have is a duplicate:
    // it earns on its own and converts to its own NFT.
    const copies = extra.copies ?? 1

    const toggle = () => (isActive ? unstake([nft]) : stake([nft]))

    return (
        <div
            className={twMerge(
                'container-inline w-full flex flex-col rounded overflow-hidden relative transition-transform hover:-translate-y-0.5',
                isActive ? 'border-gradient' : 'border border-white/20 hover:border-white/50',
                // Ahead of the active gradient on purpose. Granted skins are
                // all earning, so the gradient marks every one of them and
                // distinguishes nothing; whether a look is actually yours on
                // chain is the fact the artwork cannot show.
                !isNft && 'border-2 border-amber-400 hover:border-amber-300'
            )}
        >
            {/* Card type scales with the card, but with a floor — pure cqi sizing became
                unreadable in the narrow two-column phone layout. */}
            <div className='text-[max(0.7rem,5.5cqi)] flex flex-col'>
                <header className='flex flex-row items-center gap-[0.75em] px-[1em] py-[0.75em] font-black uppercase'>
                    <span className='flex-1 truncate' title={itemName(nft)}>
                        {itemName(nft)}
                    </span>
                    {copies > 1 && (
                        <span
                            title={`${copies} copies — each earns and each converts separately`}
                            className='text-[0.7em] px-[0.6em] py-[0.15em] rounded-full bg-white/20'
                        >
                            ×{copies}
                        </span>
                    )}
                    {nft.is_legendary && (
                        <span
                            title='Legendary'
                            className='text-[0.7em] px-[0.75em] py-[0.15em] rounded-full bg-[#FFE500] text-black'
                        >
                            LEG
                        </span>
                    )}
                </header>

                <button
                    onClick={() => setShowing(true)}
                    title={`${itemName(nft)} — click for details`}
                    className='relative block w-full cursor-pointer'
                    style={{ aspectRatio: '1/1' }}
                >
                    <ItemImage url={assetUrl(nft)} alt={itemName(nft)} />
                    <RateBadge rate={nft.stake_rate} muted={!isActive} />
                </button>

                {converting && (
                    <ConvertToNft
                        nft={nft}
                        onClose={() => setConverting(false)}
                        // Re-read the inventory: a completed conversion flips
                        // `is_nft`, which is what drops the yellow border and
                        // the Convert button. Without this the card keeps
                        // offering to convert something already converted.
                        onQueued={() => {
                            // The inventory, so a converted skin stops offering
                            // to convert, and the balance, because the fee has
                            // just been debited and the header would otherwise
                            // keep showing what the player had before paying.
                            queryClient.invalidateQueries({
                                queryKey: ['/api/nft/staking/user-nfts'],
                            })
                            queryClient.invalidateQueries({ queryKey: ['balance'] })
                            queryClient.invalidateQueries({ queryKey: ['viewer'] })
                        }}
                    />
                )}

                {showing && (
                    <ItemDetail
                        nft={nft}
                        onClose={() => setShowing(false)}
                        // Present only for a skin. Closes the detail first so
                        // the offer is not a panel on top of a panel.
                        onConvert={
                            isNft
                                ? undefined
                                : () => {
                                      setShowing(false)
                                      setConverting(true)
                                  }
                        }
                    />
                )}

                {!isNft ? (
                    <button
                        onClick={() => setConverting(true)}
                        className='p-[0.9em] min-h-[2.75rem] font-black uppercase tracking-wide bg-amber-500/90 text-black hover:bg-amber-400'
                    >
                        Convert to NFT
                    </button>
                ) : (
                    <button
                        disabled={isLoading || !canStake}
                        title={
                            canStake
                                ? undefined
                                : 'This weapon is held on chain by another wallet, so it cannot earn here'
                        }
                        onClick={toggle}
                        className={twMerge(
                            // min-h keeps it a 44px tap target even in the narrow phone grid, where
                            // em-based padding alone came out at 37px.
                            'flex flex-row items-center justify-center gap-[0.5em] p-[0.9em] min-h-[2.75rem]',
                            'font-black uppercase tracking-wide disabled:opacity-50',
                            isActive ? 'bg-white/10 hover:bg-white/20' : 'bg-br1-gradient hover:brightness-125'
                        )}
                    >
                        <span>{isActive ? 'Deactivate' : 'Activate'}</span>
                        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    </button>
                )}
            </div>
        </div>
    )
}

const RateBadge = ({ rate, muted }: { rate: number; muted: boolean }) => {
    if (!rate) return null
    return (
        <div
            className={twMerge(
                'absolute bottom-0 inset-x-0 flex flex-row items-center gap-[0.5em] p-[0.4em]',
                'font-black bg-black/70',
                muted && 'opacity-50'
            )}
        >
            <Icons.SkullPoint className='h-[2em] w-[2em] shrink-0' />
            <span>{rate} PTS / DAY</span>
        </div>
    )
}
